import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container,
  Avatar,
  Grid
} from '@mui/material';
import { VpnKey as KeyIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';

const ChangePasswordPage = ({ isForced = false }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // 입력 시 에러 메시지 제거
    if (error) setError('');
    if (success) setSuccess('');
  };

  const validateForm = () => {
    if (!formData.currentPassword || !formData.newPassword || !formData.confirmPassword) {
      setError('모든 필드를 입력해주세요.');
      return false;
    }

    if (formData.newPassword.length < 8) {
      setError('새 비밀번호는 최소 8자 이상이어야 합니다.');
      return false;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError('새 비밀번호와 확인 비밀번호가 일치하지 않습니다.');
      return false;
    }

    if (formData.currentPassword === formData.newPassword) {
      setError('새 비밀번호는 현재 비밀번호와 달라야 합니다.');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      await apiService.changePassword({
        current_password: formData.currentPassword,
        new_password: formData.newPassword
      });

      setSuccess('비밀번호가 성공적으로 변경되었습니다.');
      
      // 강제 비밀번호 변경인 경우 로그아웃 후 다시 로그인 유도
      if (isForced) {
        setTimeout(async () => {
          await logout();
          navigate('/login');
        }, 2000);
      } else {
        // 일반 비밀번호 변경인 경우 설정 페이지로 돌아가기
        setTimeout(() => {
          navigate('/settings');
        }, 2000);
      }
    } catch (err) {
      setError(err.response?.data?.detail || '비밀번호 변경 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (isForced) {
      // 강제 변경 모드에서는 로그아웃
      logout();
      navigate('/login');
    } else {
      // 일반 모드에서는 설정 페이지로 돌아가기
      navigate('/settings');
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
              <KeyIcon />
            </Avatar>
            <Typography component="h1" variant="h5">
              {isForced ? '초기 비밀번호 변경' : '비밀번호 변경'}
            </Typography>
            
            {isForced && (
              <Alert severity="warning" sx={{ width: '100%', mt: 2 }}>
                보안을 위해 초기 비밀번호를 변경해주세요.
              </Alert>
            )}
            
            {error && (
              <Alert severity="error" sx={{ width: '100%', mt: 2 }}>
                {error}
              </Alert>
            )}
            
            {success && (
              <Alert severity="success" sx={{ width: '100%', mt: 2 }}>
                {success}
              </Alert>
            )}
            
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3, width: '100%' }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    name="currentPassword"
                    label="현재 비밀번호"
                    type="password"
                    id="currentPassword"
                    value={formData.currentPassword}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    name="newPassword"
                    label="새 비밀번호"
                    type="password"
                    id="newPassword"
                    value={formData.newPassword}
                    onChange={handleChange}
                    disabled={loading}
                    helperText="최소 8자 이상"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    name="confirmPassword"
                    label="새 비밀번호 확인"
                    type="password"
                    id="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </Grid>
              </Grid>
              
              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading || !!success}
                  startIcon={loading ? <CircularProgress size={20} /> : null}
                >
                  {loading ? '변경 중...' : '비밀번호 변경'}
                </Button>
                
                {!isForced && (
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleCancel}
                    disabled={loading || !!success}
                  >
                    취소
                  </Button>
                )}
              </Box>
              
              {isForced && (
                <Button
                  fullWidth
                  variant="text"
                  color="error"
                  onClick={handleCancel}
                  disabled={loading || !!success}
                  sx={{ mt: 1 }}
                >
                  나중에 변경하기 (로그아웃)
                </Button>
              )}
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default ChangePasswordPage; 